import React, { useMemo } from 'react';
import { OneWayInServiceDto, OneWayInServiceProviderDto } from 'src/api/v2';
import useOneWayInServices from 'src/features/one-way-in/hooks/use-one-way-in-services';
import DataTableV3 from 'src/components/DataTableV3';
import { serviceColumns } from '../../constants/service-columns';
import ServiceInfo from './ServiceInfo';
import { useCluster } from 'src/features/cluster';
import { Counties } from 'src/features/one-way-in/constants/counties';
import { useTranslation } from 'react-i18next';
import { DataTableV3Ids } from 'src/constants/dataTableIds';
import useOneWayInServiceProviders from 'src/features/one-way-in/hooks/use-one-way-in-service-providers';

type Props = {
  selectedActor?: OneWayInServiceProviderDto;
  servicesByCounty?: boolean;
  setSelectedServices?: React.Dispatch<React.SetStateAction<OneWayInServiceDto[]>>;
  headingLevel?: 'h1' | 'h2' | 'h3';
};

const ServiceTable: React.FC<Props> = ({
  selectedActor,
  servicesByCounty = false,
  setSelectedServices,
  headingLevel = 'h3',
}) => {
  const { cluster } = useCluster();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { activeClusterServices, isLoading } = useOneWayInServices(selectedActor ? selectedActor.id : '');
  const { allActiveServices } = useOneWayInServices();
  const { serviceProviders } = useOneWayInServiceProviders();

  const countyServiceProviderIds = useMemo(() => {
    return serviceProviders?.filter((sp) => sp.county === cluster?.address?.county).map((sp) => sp.id) || [];
  }, [cluster?.address?.county, serviceProviders]);

  const countyServicesByMunicipality = React.useMemo(() => {
    const services = allActiveServices.map((service) => ({
      ...service,
      counties: service.municipalities
        .map((municipality) =>
          Counties.find((county) =>
            county.municipalities.some((countyMunicipality) => countyMunicipality === municipality),
          ),
        )
        .filter((county) => county)
        .map((county) => county!.countyName)
        .filter((value, index, array) => array.indexOf(value) === index),
    }));
    const countyServices = services.filter((service) => {
      return service.counties.some((county) => cluster?.address?.county?.startsWith(county));
    });

    return countyServices as OneWayInServiceDto[];
  }, [allActiveServices, cluster]);

  const countyServices = useMemo(
    () =>
      countyServicesByMunicipality
        .concat(
          allActiveServices.filter((service) => countyServiceProviderIds.includes(service.clusterId ?? '')),
        )
        .filter((value, index, array) => array.indexOf(value) === index),
    [allActiveServices, countyServiceProviderIds, countyServicesByMunicipality],
  );

  const Heading = () =>
    React.createElement(
      headingLevel,
      { className: 'margin-bottom--1' },
      `${tOneWayIn('referring.servicesIn')} ${cluster?.address?.county}`,
    );

  if (servicesByCounty && isLoading) return <></>;

  return (
    <>
      {!servicesByCounty ? (
        <>
          <p>
            {tOneWayIn('referring.chosenActorForReferral')} <b>{selectedActor?.name}</b>
          </p>
        </>
      ) : (
        <Heading />
      )}

      {servicesByCounty && countyServices.length === 0 ? (
        <p>{tOneWayIn('frontpage.countyServices.none')}</p>
      ) : (
        <DataTableV3
          id={DataTableV3Ids.OneWayIn.OpportunityReferringServiceInfo}
          disableGlobalFilter={true}
          hideFilterText
          data={servicesByCounty ? countyServices : activeClusterServices}
          columns={serviceColumns}
          pageSizeOptions={[5, 10, 25, 100]}
          pageSize={25}
          disableColumnEditor
          disableExport
          rowsSelectedSingularTranslation={tOneWayIn('referring.servicesTable.selectedRowsSingular')}
          rowsSelectedPluralTranslation={tOneWayIn('referring.servicesTable.selectedRowsPlural')}
          showRowsSelectedText={servicesByCounty ? false : true}
          disableSelection={servicesByCounty ? true : false}
          getRowCanExpand={() => true}
          renderSubComponent={ServiceInfo}
          setSelectedRows={setSelectedServices}
          loading={isLoading}
        />
      )}
    </>
  );
};

export default ServiceTable;
